import React, { useState } from "react";
import { DesignThinkingStage, DesignThinkingTool } from "../../types";
import DoubleDiamondNavigation from "./DoubleDiamondNavigation";
import DesignThinkingToolCard from "./DesignThinkingToolCard";
import { useDesignThinkingTools } from "../../hooks/useDesignThinkingTools";
import { useGuide } from "../../hooks/useGuide";

interface DesignThinkingToolsProps {
  isActive: boolean;
}

const DesignThinkingTools: React.FC<DesignThinkingToolsProps> = ({
  isActive,
}) => {
  const [activeStage, setActiveStage] =
    useState<DesignThinkingStage>("discover");
  const { designThinkingTools, loading, error } = useDesignThinkingTools();
  const { guide, loading: guideLoading, error: guideError } = useGuide();

  const handleDownload = async (
    e: React.MouseEvent<HTMLAnchorElement>,
    url: string,
    filename: string,
  ) => {
    e.preventDefault();
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed, opening in new tab", error);
      window.open(url, "_blank");
    }
  };

  // Convert API data to the format expected by the component
  const getStageData = (stageName: string) => {
    // Map stage names to API IDs based on the provided data structure
    const stageIdMap: Record<string, number> = {
      discover: 1,
      define: 2,
      develop: 3,
      deliver: 4,
    };

    const stageId = stageIdMap[stageName];
    const stage = designThinkingTools.find((s) => s.id === stageId);
    if (!stage) return [];

    return stage.resources.map((resource) => ({
      name: resource.name,
      url: resource.link,
      description: resource.description,
      logoLetter: resource.name.trim().charAt(0).toUpperCase(),
      image: resource.image || undefined,
    }));
  };

  const getStageDescription = (stageName: string) => {
    // Map stage names to API IDs based on the provided data structure
    const stageIdMap: Record<string, number> = {
      discover: 1,
      define: 2,
      develop: 3,
      deliver: 4,
    };

    const stageId = stageIdMap[stageName];
    const stage = designThinkingTools.find((s) => s.id === stageId);
    return stage?.description || "";
  };

  const getStageTitle = (stageName: string) => {
    // Map stage names to API IDs based on the provided data structure
    const stageIdMap: Record<string, number> = {
      discover: 1,
      define: 2,
      develop: 3,
      deliver: 4,
    };

    const stageId = stageIdMap[stageName];
    const stage = designThinkingTools.find((s) => s.id === stageId);
    return (
      stage?.name || stageName.charAt(0).toUpperCase() + stageName.slice(1)
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="alert alert-warning" role="alert">
          Unable to load tools from API. Error: {error}
        </div>
      );
    }

    if (designThinkingTools.length === 0) {
      return (
        <div className="alert alert-info" role="alert">
          No tools available from API.
        </div>
      );
    }

    return (
      <>
        <DoubleDiamondNavigation
          activeStage={activeStage}
          onStageChange={setActiveStage}
          designThinkingTools={designThinkingTools}
        />

        <div
          id="dd-stage-descriptions"
          className="mt-5 mb-4"
          style={{ maxWidth: "880px", margin: "0 auto 2.5rem" }}
        >
          {(
            [
              "discover",
              "define",
              "develop",
              "deliver",
            ] as DesignThinkingStage[]
          ).map((stage) => (
            <div
              key={stage}
              className={`dd-stage-description dd-desc-${stage} ${
                activeStage === stage ? "" : "d-none"
              }`}
            >
              <h3 className="fw-bold mb-2">{getStageTitle(stage)}</h3>
              <p>{getStageDescription(stage)}</p>
            </div>
          ))}
        </div>

        <div id="dd-tools-grids">
          {(
            [
              "discover",
              "define",
              "develop",
              "deliver",
            ] as DesignThinkingStage[]
          ).map((stage: DesignThinkingStage) => (
            <div
              key={stage}
              className={`dd-tools-grid dd-tools-${stage} ${
                activeStage === stage ? "" : "d-none"
              }`}
            >
              {getStageData(stage).map(
                (tool: DesignThinkingTool, index: number) => (
                  <DesignThinkingToolCard key={index} tool={tool} />
                ),
              )}
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div
      className="dd-main-tab-panel dd-panel-tools"
      style={{ display: isActive ? "block" : "none" }}
    >
      <div
        className="blog-details__content"
        style={{
          background: "#fff",
          borderRadius: "16px",
          boxShadow: "0 8px 28px -10px rgba(0,40,100,.12)",
          padding: "2rem",
        }}
      >
        {renderContent()}

        <div className="dd-guidebooks-section mt-5">
          {guideLoading ? (
            <div className="text-center py-3">
              <div
                className="spinner-border spinner-border-sm text-primary"
                role="status"
              >
                <span className="visually-hidden">Loading guidebooks...</span>
              </div>
            </div>
          ) : guideError ? (
            <div className="alert alert-warning" role="alert">
              Unable to load guidebooks. Error: {guideError}
            </div>
          ) : guide.length === 0 ? (
            <div className="alert alert-info" role="alert">
              No guidebooks available.
            </div>
          ) : (
            guide.map((guideItem, index) => (
              <div
                key={guideItem.id}
                className={`dd-guidebook-card${index < guide.length - 1 ? " mb-4" : ""}`}
              >
                <div className="row align-items-center g-4">
                  <div className="col-md-8">
                    <div className="blog-details__text">
                      <p>{guideItem.introduction}</p>
                      <div className="d-flex flex-wrap gap-2 align-items-center justify-content-start mt-3">
                        <a
                          className="btn-main"
                          href={guideItem.file}
                          onClick={(e) => {
                            const fileName = decodeURIComponent(
                              guideItem.file.split("/").pop()?.split("?")[0] ||
                                `Guidebook_${index + 1}.pdf`,
                            );
                            handleDownload(e, guideItem.file, fileName);
                          }}
                          download
                        >
                          Download PDF
                        </a>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="dd-guidebook-image-wrapper">
                      <img
                        src={guideItem.book_cover}
                        alt={`AI for Design Thinking Guidebook Cover ${index + 1}`}
                        className="dd-guidebook-image"
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default DesignThinkingTools;
